import { Link } from "react-router-dom";
import { Download, ArrowRight } from "lucide-react";
import ServiceLayout, { ContentSection } from "@/components/site/ServiceLayout";
import { Reveal, TickList } from "@/components/site/Reveal";
import { API, GENERAL_FORM_URL } from "@/data/services";

const SERVICES_LIST = [
  "Functional Behaviour Assessments",
  "Interim Behaviour Support Plans",
  "Comprehensive Behaviour Support Plans",
  "Behaviour data collection and analysis",
  "Participant and stakeholder consultation",
  "Training for families, carers and support teams",
  "Restrictive practice review and reduction",
  "Plan implementation and monitoring",
  "Progress reports",
  "NDIS review recommendations",
];

const PROCESS = [
  { n: "01", title: "Referral and service agreement", text: "We confirm suitability, funding and scope, and complete a service agreement." },
  { n: "02", title: "Initial assessment", text: "Getting to know the participant, their goals and their support network." },
  { n: "03", title: "Observation and information gathering", text: "Direct observation, data collection and consultation with everyone involved." },
  { n: "04", title: "Behaviour support plan", text: "A practical, person-centred plan everyone can understand and use." },
  { n: "05", title: "Training and implementation", text: "Hands-on training for families, carers and support teams." },
  { n: "06", title: "Monitoring and review", text: "Ongoing data review, plan refinement and NDIS review recommendations." },
];

export default function NdisBehaviourSupport() {
  return (
    <ServiceLayout
      eyebrow="Services"
      title="NDIS Positive Behaviour Support"
      lead="Specialist Positive Behaviour Support focused on understanding behaviour, improving quality of life and strengthening the capacity of participants and the people around them."
      image="https://images.unsplash.com/photo-1512716679859-da19b4af9c38?crop=entropy&cs=srgb&fm=jpg&q=85"
      imageAlt="A calm, ordered space with natural light"
    >
      <ContentSection eyebrow="What it means" title="Understanding behaviour, not managing people">
        <Reveal>
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-mutedsoft">
            Positive Behaviour Support (PBS) is an evidence-informed approach that seeks to understand why behaviour
            occurs, and to improve quality of life by building skills, adjusting environments and strengthening the
            capacity of participants and their support networks. The goal is always greater independence, dignity
            and connection — and the reduction of restrictive practices wherever possible.
          </p>
          <p className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-mutedsoft">
            Our approach emphasises comprehensive assessment, understanding the function and context of behaviour,
            and the development of functionally equivalent skills — improving each person's autonomy, participation,
            relationships, safety and overall quality of life. We are committed to person-centred practice,
            supported decision-making, safeguarding human rights, and reducing and eliminating restrictive practices
            wherever possible.
          </p>
        </Reveal>
      </ContentSection>

      <ContentSection eyebrow="Who we work with" title="Participants and the people around them" pale>
        <TickList
          items={[
            "NDIS participants with behaviour support funding (Capacity Building — Improved Relationships)",
            "Families, carers and informal supports",
            "Support workers and provider teams",
            "Support coordinators and plan managers",
            "Schools and other services involved in the participant's life",
          ]}
        />
      </ContentSection>

      <ContentSection eyebrow="Services" title="Behaviour support services available">
        <TickList items={SERVICES_LIST} />
      </ContentSection>

      <ContentSection eyebrow="The process" title="From referral to review" pale>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="ndis-process">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.06}>
              <div className="h-full border-t-2 border-euca-line pt-6">
                <span className="font-serif text-4xl font-medium italic text-euca-sage/70">{p.n}</span>
                <h3 className="mt-4 font-serif text-xl font-medium text-euca-dark">{p.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-mutedsoft">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </ContentSection>

      <ContentSection eyebrow="Locations & delivery" title="Where behaviour support happens">
        <Reveal>
          <p className="max-w-3xl text-base leading-relaxed text-mutedsoft">
            Behaviour support is delivered where it matters most — in the participant's home, community settings,
            schools and supported accommodation across the Gold Coast, Logan, Brisbane and Canberra, with telehealth
            used where appropriate.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={GENERAL_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ndis-referral-button"
              className="inline-flex items-center gap-2 rounded-full bg-euca px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-euca-dark"
            >
              Make an NDIS Referral <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`${API}/downloads/ndis-referral-form`}
              data-testid="ndis-download-form-button"
              className="inline-flex items-center gap-2 rounded-full border border-euca px-7 py-3.5 text-sm font-semibold text-euca transition-all duration-300 hover:bg-euca hover:text-white"
            >
              Download Referral Form <Download className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </ContentSection>
    </ServiceLayout>
  );
}
